import { TournamentLeaderRow, TournamentMe } from '../api/tournamentsApi.type';

export const TOURNAMENT_TABS = [ '/tournaments', '/tournaments/finished', '/tournaments/my' ] as const;

export type TournamentTab = typeof TOURNAMENT_TABS[number];

export interface MarkedLeaderRow extends TournamentLeaderRow {
  isMe: boolean;
}

export function unwrapData(response: unknown): unknown {
  if (response !== null && typeof response === 'object' && !Array.isArray(response)) {
    const keys = Object.keys(response);
    if (keys.length === 1 && keys[0] === 'data') {
      return (response as { data: unknown }).data;
    }
  }
  return response;
}

export function toList<T>(response: unknown): T[] {
  const value = unwrapData(response);
  return Array.isArray(value) ? value as T[] : [];
}

export function parseTournamentId(value: string | undefined): number | null {
  if (!value || !/^[0-9]+$/.test(value)) {
    return null;
  }
  const id = Number(value);
  return id > 0 && Number.isSafeInteger(id) ? id : null;
}

export function serverOffset(serverTime: number, receivedAt: number | undefined): number {
  if (!serverTime || !receivedAt) {
    return 0;
  }
  return serverTime * 1000 - receivedAt;
}

const amountFormat = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });

export function formatAmount(value: number): string {
  return amountFormat.format(value);
}

export function currencyLabel(code: string | null | undefined, somoni: string): string {
  if (!code) {
    return '';
  }
  return code === 'TJS' ? somoni : code;
}

export function formatOdd(value: number): string {
  return Number(value).toFixed(2);
}

function isMeRow(row: TournamentLeaderRow, me: TournamentMe): boolean {
  if (me.walletNo !== undefined && me.walletNo !== null && row.walletNo !== null) {
    return row.walletNo === me.walletNo;
  }
  return row.place === me.place;
}

export function markLeaderboard(rows: TournamentLeaderRow[], me: TournamentMe | null): { rows: MarkedLeaderRow[], pinned: MarkedLeaderRow | null } {
  const marked = rows.map((row) => ({ ...row, isMe: me !== null && isMeRow(row, me) }));
  if (me === null || marked.some((row) => row.isMe)) {
    return { rows: marked, pinned: null };
  }
  return {
    rows: marked,
    pinned: {
      place: me.place,
      walletNo: me.walletNo ?? null,
      points: me.points,
      prize: null,
      isMe: true,
    },
  };
}

export function podium(rows: TournamentLeaderRow[]): (TournamentLeaderRow | null)[] {
  const byPlace = (place: number) => rows.find((row) => row.place === place) ?? null;
  return [ byPlace(2), byPlace(1), byPlace(3) ];
}

export function resolveTab(pathname: string, stateTab: unknown): TournamentTab | false {
  const clean = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const direct = TOURNAMENT_TABS.find((tab) => tab === clean);
  if (direct) {
    return direct;
  }
  return TOURNAMENT_TABS.find((tab) => tab === stateTab) ?? false;
}

export function rulesFallbackPath(rulesUrl: string): string | null {
  const match = /^(.*\/)([a-z]{2})(\/[^/]+\.html)$/.exec(rulesUrl);
  if (!match || match[2] === 'ru') {
    return null;
  }
  return `${match[1]}ru${match[3]}`;
}

export function hasErrorStatus(error: unknown, statuses: number[]): boolean {
  if (error === null || typeof error !== 'object' || !('status' in error)) {
    return false;
  }
  const status = (error as { status: unknown }).status;
  return typeof status === 'number' && statuses.includes(status);
}
